import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationService } from '@shared/services/notification.service';
import { AppState } from '@modules/core/models/app-state';
import { UserMapConfig } from '@modules/core/models/user-map-config';
import { AuthService } from '@modules/core/services';
import { AppStateService } from '@modules/core/services/app-state.service';
import { HomeWidgetVisibilityStore } from '@modules/core/store/home-widget-visibility.store';
import { LanguageStore } from '@modules/core/store';
import { VEHICLE_COLOR_RGB, VehiclePunctualityConfig, VehicleWarningConfig, WarningMessageType } from '@modules/shared/models/vehicle';
import { ExpandableSectionComponent } from '@modules/shared/components/expandable-section/expandable-section.component';
import { WarningMessageTypePipe } from '@modules/shared/pipes/warning-message.pipe';
import { ConfigService } from 'src/config';
import { TrafficDeviationsConfig } from '@components/traffic-deviations/models/traffic-deviations-config';
import { HOME_VIEW_WIDGET_SETTINGS, HomeViewWidgetSettingViewModel } from './settings-overview.models';

@Component({
    selector: 'app-settings-overview',
    templateUrl: './settings-overview.component.html',
    styleUrls: ['./settings-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ExpandableSectionComponent,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        FormsModule,
        MatSlideToggleModule,
        TranslateModule,
        WarningMessageTypePipe
    ]
})
export class SettingsOverviewComponent implements OnInit {
    private readonly _appState = inject(AppStateService);
    private readonly _languageStore = inject(LanguageStore);
    private readonly _authService = inject(AuthService);
    private readonly _config = inject(ConfigService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _notification = inject(NotificationService);
    private readonly _homeWidgetVisibility = inject(HomeWidgetVisibilityStore);

    public readonly COLORS = VEHICLE_COLOR_RGB;
    public appState: AppState;
    public showDeveloperData = false;
    public punctualityConfig: VehiclePunctualityConfig;
    public warningConfig: VehicleWarningConfig;
    public mapConfig: UserMapConfig;
    public trafficDeviationsConfig: TrafficDeviationsConfig;
    public lang: string;
    public warnings: WarningMessageType[];
    public readonly homeViewWidgets = computed<HomeViewWidgetSettingViewModel[]>(() =>
        HOME_VIEW_WIDGET_SETTINGS.map(widget => ({
            ...widget,
            visible: this._homeWidgetVisibility.isVisible(widget.id),
        }))
    );

    constructor() {
        const warnings = VehicleWarningConfig.warningsThatCanBeToggledInSettings;
        const disabledWarningTypes = this._config.disabledWarningTypes;
        this.warnings = _.pullAll(warnings, disabledWarningTypes);
    }

    ngOnInit(): void {
        this._appState.getState().pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(appState => {
            this.appState = appState;

            if (!this.punctualityConfig) {
                this.punctualityConfig = appState.punctualityConfig;
            }
            if (!this.mapConfig) {
                this.mapConfig = appState.mapConfig;
            }
            if (!this.warningConfig) {
                this.warningConfig = appState.warningConfig;
            }
            if (!this.trafficDeviationsConfig) {
                this.trafficDeviationsConfig = appState.trafficDeviationsConfig;
            }
        });

        this._languageStore.getState().pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(lang => {
            this.lang = lang;
        });

        this.showDeveloperData = this._authService.hasAccessToOmsFunction('OMS Map debug');
    }

    public updatePunctualityConfig(): void {
        this._appState.setPunctualityConfig(this.punctualityConfig);
    }

    public updateMapConfig(): void {
        this._appState.setMapConfig({ ...this.mapConfig });
    }

    public updateWarningConfig(type: WarningMessageType): void {
        this.warningConfig.toggle(type);
        this._appState.setWarningConfig(this.warningConfig);
    }

    public getSwitchText(type: WarningMessageType): string {
        return this.warningConfig.isVisible(type) ? 'On' : 'Off';
    }

    public updateTrafficDeviationConfig(): void {
        this._appState.setTrafficDeviationConfig(this.trafficDeviationsConfig);
    }

    public updateHomeViewWidgetVisibility(widgetId: string, visible: boolean): void {
        this._homeWidgetVisibility.setVisible(widgetId, visible);
    }

    public resetWidgetSettings(): void {
        const prefix = 'oms.widget.';
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(prefix)) keysToRemove.push(key);
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        this._homeWidgetVisibility.reset();
        this._notification.openText('Widget layout settings have been reset.', 5000);
    }
}
